import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/shadcn/card'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from '@/components/shadcn/dropdown-menu'
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/shadcn/chart'
import { LineChart, Line, CartesianGrid, XAxis, YAxis } from 'recharts'
import { listMemoryLibraryIds } from '@/lib/api/memory'
import { fetchTelemetryTimeseries, type TelemetryTimeseriesPoint } from '@/lib/api/telemetry'
import { Filter, RefreshCw } from 'lucide-react'

function toIso(v: string): string | undefined {
  if (!v) return undefined
  try {
    const d = new Date(v)
    if (isNaN(d.getTime())) return undefined
    return d.toISOString()
  } catch {
    return undefined
  }
}

function isoToLocal(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  const yyyy = d.getFullYear()
  const mm = pad(d.getMonth() + 1)
  const dd = pad(d.getDate())
  const hh = pad(d.getHours())
  const mi = pad(d.getMinutes())
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`
}

export default function UsagePerformancePage() {
  const { t } = useTranslation('common')
  const { data: allMemoryIds } = useQuery({ queryKey: ['memory-library-ids'], queryFn: () => listMemoryLibraryIds() })
  const [selectedMemoryIds, setSelectedMemoryIds] = useState<string[]>([])
  const [reqType, setReqType] = useState('')
  const [startLocal, setStartLocal] = useState<string>(() => { const d = new Date(); d.setDate(d.getDate() - 7); return isoToLocal(d.toISOString()) })
  const [endLocal, setEndLocal] = useState<string>(() => isoToLocal(new Date().toISOString()))
  const [interval, setInterval] = useState<'minute' | 'hour' | 'day'>('day')

  const common = useMemo(() => ({
    start: toIso(startLocal),
    end: toIso(endLocal),
    req_type: reqType || undefined,
    memory_ids: (selectedMemoryIds && selectedMemoryIds.length > 0) ? selectedMemoryIds : (allMemoryIds || []),
  }), [startLocal, endLocal, reqType, selectedMemoryIds, allMemoryIds])

  const { data: ts } = useQuery({
    queryKey: ['telemetry-timeseries-performance', { ...common, interval }],
    queryFn: () => fetchTelemetryTimeseries({ ...common, interval }),
    enabled: (allMemoryIds || []).length > 0,
  })

  return (
    <div className="space-y-6 pt-8">
      <div className="flex flex-col items-stretch space-y-0 sm:flex-row">
        <div className="flex flex-1 flex-col justify-center gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t('usage.performance.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('usage.performance.description')}</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('usage.performance.filters.title')}</CardTitle>
          <CardDescription>{t('usage.performance.filters.description')}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-4">
          <div className="flex flex-col gap-2">
            <div className="text-sm text-muted-foreground">{t('usage.performance.filters.memoryVaults')}</div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="justify-between">
                  <Filter className="mr-2 h-4 w-4" />
                  {selectedMemoryIds.length === (allMemoryIds?.length || 0) ? t('usage.performance.filters.all') : t('usage.performance.filters.selected', { count: selectedMemoryIds.length })}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64 p-1">
                {(allMemoryIds || []).map((id) => (
                  <DropdownMenuCheckboxItem key={id} checked={selectedMemoryIds.includes(id)} onCheckedChange={(checked) => setSelectedMemoryIds((prev) => checked ? Array.from(new Set([...prev, id])) : prev.filter((x) => x !== id))}>
                    <code className="text-xs">{id}</code>
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex flex-col gap-2">
            <div className="text-sm text-muted-foreground">{t('usage.performance.filters.requestType')}</div>
            <Select value={reqType || '_all'} onValueChange={(v) => setReqType(v === '_all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder={t('usage.performance.filters.all')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">{t('usage.performance.filters.all')}</SelectItem>
                <SelectItem value="memory_query">memory_query</SelectItem>
                <SelectItem value="assistant_message">assistant_message</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <div className="text-sm text-muted-foreground">{t('usage.performance.filters.timeStart')}</div>
            <Input type="datetime-local" value={startLocal} onChange={(e) => setStartLocal(e.target.value)} />
          </div>
          <div className="flex flex-col gap-2">
            <div className="text-sm text-muted-foreground">{t('usage.performance.filters.timeEnd')}</div>
            <div className="flex items-center gap-2">
              <Input type="datetime-local" value={endLocal} onChange={(e) => setEndLocal(e.target.value)} />
              <Button variant="outline" size="icon" onClick={() => setEndLocal(isoToLocal(new Date().toISOString()))} title={t('usage.performance.filters.resetToCurrent')}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="text-sm text-muted-foreground">{t('usage.performance.filters.timeGranularity')}</div>
            <Select value={interval} onValueChange={(v) => (setInterval(v as any))}>
              <SelectTrigger>
                <SelectValue placeholder="day" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="minute">{t('usage.performance.filters.minute')}</SelectItem>
                <SelectItem value="hour">{t('usage.performance.filters.hour')}</SelectItem>
                <SelectItem value="day">{t('usage.performance.filters.day')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('usage.performance.charts.percentileTrends.title')}</CardTitle>
          <CardDescription>{t('usage.performance.charts.percentileTrends.description')}</CardDescription>
        </CardHeader>
        <CardContent className="px-2 sm:p-6">
          <ChartContainer config={{ avg_ms: { label: 'avg_ms', color: 'hsl(var(--chart-1))' }, p95_ms: { label: 'p95_ms', color: 'hsl(var(--chart-2))' }, p99_ms: { label: 'p99_ms', color: 'hsl(var(--chart-3))' } }} className="aspect-auto h-[320px] w-full">
            <LineChart data={ts as TelemetryTimeseriesPoint[] | undefined} margin={{ left: 8, right: 8, top: 8, bottom: 8 }}>
              <CartesianGrid vertical={false} />
              <XAxis dataKey="time" tickLine={false} axisLine={false} tickMargin={8} minTickGap={16} tickFormatter={(value) => new Date(value).toLocaleDateString('zh-CN', { hour12: false })} />
              <YAxis stroke="hsl(var(--muted-foreground))" />
              <ChartTooltip content={<ChartTooltipContent className="w-[200px]" />} />
              <Line type="monotone" dataKey="avg_ms" stroke="var(--color-avg_ms)" dot={false} />
              <Line type="monotone" dataKey="p95_ms" stroke="var(--color-p95_ms)" dot={false} />
              <Line type="monotone" dataKey="p99_ms" stroke="var(--color-p99_ms)" dot={false} />
            </LineChart>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  )
}


